import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface MediaImageProps {
    src: string
    alt: string
    sizes: string
    className?: string
}

const OPTIMIZABLE_HOST = /\.cdn\.modulify\.website$/i

const canOptimize = (src: string): boolean => {
    if (src.startsWith('/')) return true

    try {
        const url = new URL(src)

        return url.protocol === 'https:' && OPTIMIZABLE_HOST.test(url.hostname)
    }
    catch {
        return false
    }
}

// Images that come from the CMS. Files in the site's own storage are resized and optimized;
// any other address (a pasted link, for example) is shown as is so it can never break the page.
// The parent must be positioned and sized; the image fills it.
const MediaImage: FC<MediaImageProps> = ({ src, alt, sizes, className }): ReactNode => {
    if (canOptimize(src)) {
        return (
            <Image
                src={src}
                alt={alt}
                fill
                sizes={sizes}
                className={className}
            />
        )
    }

    return (
        <img
            src={src}
            alt={alt}
            loading='lazy'
            decoding='async'
            className={cn('absolute inset-0 h-full w-full', className)}
        />
    )
}

export default MediaImage
